import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { loginApi } from "src/services/authApi";
import { config } from "configs/config";
import { useNavigate, useLocation } from "react-router-dom";
import { registerUnauthorizedHandler } from "src/services/apiClient";
import { clearCache } from "src/services/apiCache";

/** Overridable in Playwright via `page.addInitScript` for scenario-10 (see `e2e/tests/scenario-10-inactivity-logout.spec.ts`). */
function readE2eMs(key: "__E2E_INACTIVITY_MS__" | "__E2E_WARNING_MS__", fallback: number): number {
    if (typeof window === "undefined") return fallback;
    const w = window as Window & { __E2E_INACTIVITY_MS__?: number; __E2E_WARNING_MS__?: number };
    const v = w[key];
    return typeof v === "number" && v > 0 ? v : fallback;
}

const INACTIVITY_MS  = readE2eMs("__E2E_INACTIVITY_MS__", 15 * 60 * 1000); // 15 minutes → auto-logout
const WARNING_MS     = readE2eMs("__E2E_WARNING_MS__", 2 * 60 * 1000); // show warning 2 minutes before
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "api:activity"] as const;

import type { Doctor, Praxis } from "src/types";

interface User {
    doctor: Doctor;
    praxis: Praxis;
}

interface AuthContextType {
    user: User | null;
    login: (x: string, u: string, p: string) => Promise<string | null>;
    logout: () => void;
    updateUser: (partial: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType>(null!);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const { t } = useTranslation();
    const navigate  = useNavigate();
    const location  = useLocation();
    const [showWarning, setShowWarning]   = useState(false);
    const [countdown,   setCountdown]     = useState(WARNING_MS / 1000);
    const logoutTimer   = useRef<ReturnType<typeof setTimeout> | null>(null);
    const warningTimer  = useRef<ReturnType<typeof setTimeout> | null>(null);
    const countdownRef  = useRef<ReturnType<typeof setInterval> | null>(null);

    const [user, setUser] = useState<User | null>(() => {
        try {
            const doctor = localStorage.getItem("doctor");
            const praxis = localStorage.getItem("praxis");
            if (doctor && praxis) {
                return {
                    doctor: JSON.parse(doctor),
                    praxis: JSON.parse(praxis),
                };
            }
        } catch {
            localStorage.removeItem("doctor");
            localStorage.removeItem("praxis");
            localStorage.removeItem("token");
        }
        return null;
    });

    const login = async (
        praxis: string,
        username: string,
        password: string,
    ) => {
        try {
            const res = await loginApi(praxis, username, password);
            if (!res.doctor || !res.praxis || !res.token) throw new Error("Invalid login response");
            localStorage.setItem("token", res.token);
            localStorage.setItem("doctor", JSON.stringify(res.doctor));
            localStorage.setItem("praxis", JSON.stringify(res.praxis));

            setUser({
                doctor: res.doctor,
                praxis: res.praxis,
            });

            return null;
        } catch (e) {
            const msg = e instanceof Error ? e.message : "UNKNOWN_ERROR";
            console.error("AuthProvider login:", msg);
            return msg;
        }
    };

    const updateUser = (partial: Partial<User>) => {
        setUser((prev) => {
            if (!prev) return prev;
            const next = { ...prev, ...partial };
            if (partial.doctor) localStorage.setItem("doctor", JSON.stringify(next.doctor));
            if (partial.praxis) localStorage.setItem("praxis", JSON.stringify(next.praxis));
            return next;
        });
    };

    const logout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("doctor");
        localStorage.removeItem("praxis");
        clearCache();

        navigate("/login");
        setUser(null);
    };

    // ── Inactivity auto-logout ────────────────────────────────────────────────
    // Tracks whether the warning is currently visible — activity events must
    // NOT reset the timer once the warning is shown, otherwise any mouse
    // movement over the overlay keeps pushing the logout indefinitely.
    const warningActiveRef = useRef(false);

    const clearTimers = useCallback(() => {
        if (logoutTimer.current)  clearTimeout(logoutTimer.current);
        if (warningTimer.current) clearTimeout(warningTimer.current);
        if (countdownRef.current) clearInterval(countdownRef.current);
    }, []);

    const resetInactivityTimers = useCallback(() => {
        if (!user) return;
        if (warningActiveRef.current) return; // warning showing — ignore activity
        clearTimers();
        setShowWarning(false);

        const logoutAt = Date.now() + INACTIVITY_MS;

        warningTimer.current = setTimeout(() => {
            warningActiveRef.current = true;
            setShowWarning(true);
            setCountdown(Math.round(WARNING_MS / 1000));
            countdownRef.current = setInterval(() => {
                setCountdown(Math.max(0, Math.round((logoutAt - Date.now()) / 1000)));
            }, 500);
        }, INACTIVITY_MS - WARNING_MS);

        logoutTimer.current = setTimeout(() => {
            warningActiveRef.current = false;
            clearTimers();
            logout();
        }, INACTIVITY_MS);
    }, [user, logout, clearTimers]);

    const stayLoggedIn = useCallback(() => {
        warningActiveRef.current = false;
        resetInactivityTimers();
    }, [resetInactivityTimers]);

    useEffect(() => {
        if (!user) { clearTimers(); setShowWarning(false); return; }
        resetInactivityTimers();
        ACTIVITY_EVENTS.forEach((e) =>
            window.addEventListener(e, resetInactivityTimers, { passive: true }),
        );
        return () => {
            clearTimers();
            ACTIVITY_EVENTS.forEach((e) =>
                window.removeEventListener(e, resetInactivityTimers),
            );
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Keep the 401 handler in sync with the current logout reference
    useEffect(() => {
        registerUnauthorizedHandler(logout);
    }, [logout]);

    return (
        <AuthContext.Provider value={{ user, login, logout, updateUser }}>
            {children}
            {showWarning && (
                <div className="inactivity-overlay">
                    <div className="inactivity-dialog">
                        <p className="inactivity-dialog__msg">
                            {t("inactivity.message", { count: countdown })}
                        </p>
                        <button
                            className="inactivity-dialog__btn"
                            onClick={stayLoggedIn}
                        >
                            {t("inactivity.stayLoggedIn")}
                        </button>
                    </div>
                </div>
            )}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
